import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { type TourStep } from '@/config/tourSteps';
import { Button } from '@/components/ui/button';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

interface TourSpotlightProps {
    step: TourStep;
    currentStep: number;
    totalSteps: number;
    onNext: () => void;
    onPrev: () => void;
    onSkip: () => void;
}

interface TargetRect {
    top: number;
    left: number;
    width: number;
    height: number;
}

export const TourSpotlight: React.FC<TourSpotlightProps> = ({
    step,
    currentStep,
    totalSteps,
    onNext,
    onPrev,
    onSkip
}) => {
    const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
    const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
    const tooltipRef = useRef<HTMLDivElement>(null);

    // Find and track target element position
    useEffect(() => {
        const updatePosition = () => {
            const target = document.querySelector(step.targetSelector);
            if (target) {
                const rect = target.getBoundingClientRect();
                setTargetRect({
                    top: rect.top + window.scrollY,
                    left: rect.left + window.scrollX,
                    width: rect.width,
                    height: rect.height
                });

                // Scroll into view if needed
                target.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            } else {
                setTargetRect(null);
            }
        };

        updatePosition();

        // Update on resize/scroll
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);

        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [step.targetSelector]);

    // Position tooltip relative to target
    useEffect(() => {
        if (!targetRect || !tooltipRef.current) return;

        const tooltip = tooltipRef.current;
        const tooltipRect = tooltip.getBoundingClientRect();
        const padding = 16;
        const arrowOffset = 12;

        let position = step.position || 'auto';

        // Auto-position based on available space
        if (position === 'auto') {
            const spaceBelow = window.innerHeight - (targetRect.top - window.scrollY + targetRect.height);
            const spaceAbove = targetRect.top - window.scrollY;
            const spaceRight = window.innerWidth - (targetRect.left + targetRect.width);
            const spaceLeft = targetRect.left;

            if (spaceBelow >= tooltipRect.height + 50) {
                position = 'bottom';
            } else if (spaceAbove >= tooltipRect.height + 50) {
                position = 'top';
            } else if (spaceRight >= tooltipRect.width + 50) {
                position = 'right';
            } else if (spaceLeft >= tooltipRect.width + 50) {
                position = 'left';
            } else {
                position = 'bottom';
            }
        }

        let style: React.CSSProperties = {};

        switch (position) {
            case 'bottom':
                style = {
                    top: targetRect.top + targetRect.height + arrowOffset,
                    left: Math.max(padding, Math.min(
                        targetRect.left + targetRect.width / 2 - tooltipRect.width / 2,
                        window.innerWidth - tooltipRect.width - padding
                    ))
                };
                break;
            case 'top':
                style = {
                    top: targetRect.top - tooltipRect.height - arrowOffset,
                    left: Math.max(padding, Math.min(
                        targetRect.left + targetRect.width / 2 - tooltipRect.width / 2,
                        window.innerWidth - tooltipRect.width - padding
                    ))
                };
                break;
            case 'left':
                style = {
                    top: targetRect.top + targetRect.height / 2 - tooltipRect.height / 2,
                    left: targetRect.left - tooltipRect.width - arrowOffset
                };
                break;
            case 'right':
                style = {
                    top: targetRect.top + targetRect.height / 2 - tooltipRect.height / 2,
                    left: targetRect.left + targetRect.width + arrowOffset
                };
                break;
        }

        setTooltipStyle(style);
    }, [targetRect, step.position]);

    const isFirstStep = currentStep === 0;
    const isLastStep = currentStep === totalSteps - 1;

    const overlayContent = (
        <div className="fixed inset-0 z-[9999]">
            {/* Dimmed backdrop with spotlight cutout */}
            <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
                <defs>
                    <mask id="spotlight-mask">
                        <rect x="0" y="0" width="100%" height="100%" fill="white" />
                        {targetRect && (
                            <rect
                                x={targetRect.left - 8}
                                y={targetRect.top - 8}
                                width={targetRect.width + 16}
                                height={targetRect.height + 16}
                                rx="8"
                                fill="black"
                            />
                        )}
                    </mask>
                </defs>
                <rect
                    x="0"
                    y="0"
                    width="100%"
                    height="100%"
                    fill="rgba(0, 0, 0, 0.75)"
                    mask="url(#spotlight-mask)"
                />
            </svg>

            {/* Spotlight ring around target */}
            {targetRect && (
                <div
                    className="absolute border-2 border-primary rounded-lg pointer-events-none animate-pulse"
                    style={{
                        top: targetRect.top - 8,
                        left: targetRect.left - 8,
                        width: targetRect.width + 16,
                        height: targetRect.height + 16,
                        boxShadow: '0 0 0 4px rgba(29, 107, 205, 0.3)'
                    }}
                />
            )}

            {/* Tooltip */}
            <div
                ref={tooltipRef}
                className="absolute bg-white rounded-xl shadow-2xl p-5 w-80 max-w-[calc(100vw-32px)] animate-in fade-in slide-in-from-bottom-2 duration-300"
                style={tooltipStyle}
            >
                {/* Skip button */}
                <button
                    onClick={onSkip}
                    className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Skip tour"
                >
                    <X className="h-5 w-5" />
                </button>

                {/* Step indicator */}
                <div className="text-xs font-medium text-primary mb-2">
                    Step {currentStep + 1} of {totalSteps}
                </div>

                {/* Content */}
                <h3 className="text-lg font-bold text-gray-900 mb-2 pr-6">
                    {step.title}
                </h3>
                <p className="text-sm text-gray-600 mb-5 leading-relaxed">
                    {step.description}
                </p>

                {/* Progress dots */}
                <div className="flex justify-center gap-1.5 mb-4">
                    {Array.from({ length: totalSteps }).map((_, i) => (
                        <div
                            key={i}
                            className={`w-2 h-2 rounded-full transition-colors ${i === currentStep
                                ? 'bg-primary'
                                : i < currentStep
                                    ? 'bg-primary/40'
                                    : 'bg-gray-200'
                                }`}
                        />
                    ))}
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onPrev}
                        disabled={isFirstStep}
                        className={isFirstStep ? 'invisible' : ''}
                    >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Back
                    </Button>

                    <Button
                        size="sm"
                        onClick={onNext}
                        className="px-6"
                    >
                        {isLastStep ? 'Finish' : 'Next'}
                        {!isLastStep && <ChevronRight className="h-4 w-4 ml-1" />}
                    </Button>
                </div>
            </div>

            {/* Click outside to skip prompt */}
            <div
                className="absolute inset-0"
                onClick={(e) => {
                    // Only skip if clicking on the backdrop, not the tooltip
                    if (e.target === e.currentTarget) {
                        // Optional: could show a confirmation or just skip
                    }
                }}
                style={{ pointerEvents: 'auto', zIndex: -1 }}
            />
        </div>
    );

    return createPortal(overlayContent, document.body);
};
